import React, { useEffect, useRef } from 'react';

interface DotFieldProps {
  className?: string;
  /** base dot color as "r,g,b" */
  baseColor?: string;
  /** color dots blend to near the cursor, as "r,g,b" */
  glowColor?: string;
  /** grid spacing in px */
  gap?: number;
  /** cursor influence radius in px */
  influence?: number;
}

const parseRgb = (s: string): [number, number, number] => {
  const [r, g, b] = s.split(',').map(n => parseInt(n.trim(), 10));
  return [r || 0, g || 0, b || 0];
};

/**
 * Canvas grid of dots that swell and change color near the cursor,
 * with a slow idle wave so it never looks dead (and still reads on touch).
 * Render it absolutely inside a `relative` parent — the parent is the
 * hover-reactive area.
 */
const DotField: React.FC<DotFieldProps> = ({
  className,
  baseColor = '124,92,255',
  glowColor = '227,160,8',
  gap = 26,
  influence = 150,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const parent = canvas?.parentElement;
    if (!canvas || !parent) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const base = parseRgb(baseColor);
    const glow = parseRgb(glowColor);
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    let w = 0;
    let h = 0;
    let raf = 0;
    let running = false;
    let t = 0;
    const mouse = { x: -9999, y: -9999 };

    const resize = () => {
      const r = parent.getBoundingClientRect();
      w = r.width;
      h = r.height;
      canvas.width = Math.max(1, Math.round(w * dpr));
      canvas.height = Math.max(1, Math.round(h * dpr));
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const mix = (a: number, b: number, p: number) => Math.round(a + (b - a) * p);

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      t += 0.016;
      for (let y = gap / 2; y < h; y += gap) {
        for (let x = gap / 2; x < w; x += gap) {
          const d = Math.hypot(x - mouse.x, y - mouse.y);
          const p = Math.max(0, 1 - d / influence);
          const pe = p * p;
          const wave = reduced ? 0.35 : Math.sin(t * 1.3 + x * 0.014 + y * 0.02) * 0.5 + 0.5;
          const radius = 1 + wave * 0.7 + pe * 3.4;
          const alpha = 0.13 + wave * 0.1 + pe * 0.65;
          ctx.beginPath();
          ctx.fillStyle = `rgba(${mix(base[0], glow[0], pe)},${mix(base[1], glow[1], pe)},${mix(
            base[2],
            glow[2],
            pe
          )},${alpha})`;
          ctx.arc(x, y, radius, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    };

    const loop = () => {
      if (!running) return;
      draw();
      raf = requestAnimationFrame(loop);
    };

    resize();
    const ro = new ResizeObserver(() => {
      resize();
      if (!running) draw();
    });
    ro.observe(parent);

    const onMove = (e: MouseEvent) => {
      const r = canvas.getBoundingClientRect();
      mouse.x = e.clientX - r.left;
      mouse.y = e.clientY - r.top;
    };
    const onLeave = () => {
      mouse.x = -9999;
      mouse.y = -9999;
    };
    parent.addEventListener('mousemove', onMove);
    parent.addEventListener('mouseleave', onLeave);

    // Only burn frames while on screen
    const io = new IntersectionObserver(([entry]) => {
      const visible = entry.isIntersecting;
      if (visible && !running && !reduced) {
        running = true;
        loop();
      } else if (!visible) {
        running = false;
        cancelAnimationFrame(raf);
      }
    });
    io.observe(canvas);

    if (reduced) draw();

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
      parent.removeEventListener('mousemove', onMove);
      parent.removeEventListener('mouseleave', onLeave);
    };
  }, [baseColor, glowColor, gap, influence]);

  return (
    <canvas
      ref={canvasRef}
      className={`pointer-events-none ${className ?? ''}`}
      aria-hidden='true'
    />
  );
};

export default DotField;
